import React, { useEffect, useState } from "react";

/**
 * StatusBar — discreet top-left overlay showing at a glance:
 *  - Trade mode (DEMO / LIVE) + colour-coded warning
 *  - Capital.com broker health (online / offline)
 *  - Current ElevenLabs voice ID (short)
 *  - Telegram link status
 *
 * Polls /api/status (public) + /api/jarvis/trade_mode (authed) every 30s.
 * Owner can click DEMO/LIVE pill to flip the broker mode instantly.
 */
export default function StatusBar({ onOpenPanel } = {}) {
  const [status, setStatus] = useState(null);
  const [mode, setMode] = useState(null);
  const [voiceName, setVoiceName] = useState("");
  const [busy, setBusy] = useState(false);
  // Collapsed by default — single rollup pill that expands on click. Stops
  // the 5-pill row obstructing panels (was overlapping show_commands etc.)
  const [expanded, setExpanded] = useState(false);

  const fetchAll = async () => {
    try {
      const tk = localStorage.getItem("rawco_token");
      const headers = { Authorization: `Bearer ${tk}` };
      const [s, m] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/status`).then(r => r.json()).catch(() => null),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jarvis/trade_mode`, { headers }).then(r => r.json()).catch(() => null),
      ]);
      setStatus(s);
      setMode(m);
      // Voice name lookup — only once at startup (cheap, cached)
      if (!voiceName && s?.active_voice_id && tk) {
        try {
          const v = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/elevenlabs/voices`, { headers });
          if (v.ok) {
            const j = await v.json();
            const f = (j.voices || []).find(x => x.voice_id === s.active_voice_id);
            setVoiceName(f ? f.name : s.active_voice_id.slice(0, 12));
          } else {
            setVoiceName(s.active_voice_id.slice(0, 12));
          }
        } catch {
          setVoiceName(s.active_voice_id.slice(0, 12));
        }
      }
    } catch {}
  };

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 30000);
    return () => clearInterval(id);
  }, []);

  const flipMode = async () => {
    if (busy || !mode) return;
    setBusy(true);
    try {
      const tk = localStorage.getItem("rawco_token");
      const next = mode.mode === "demo" ? "live" : "demo";
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jarvis/set_trade_mode`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
        body: JSON.stringify({ mode: next }),
      });
      const j = await r.json();
      setMode(j);
    } finally { setBusy(false); }
  };

  if (!status) return null;
  // Default to LIVE when mode hasn't loaded yet — owner explicitly doesn't
  // want DEMO showing transiently.
  const isLive = mode ? mode.mode === "live" : true;
  const broker = status.capital_configured ? "ONLINE" : "OFFLINE";
  // Health rollup: one bad signal → amber, two+ → red
  const anyOffline = broker !== "ONLINE" || !status.telegram_configured || !status.raw_hub_configured;
  return (
    <div className={`status-bar ${expanded ? "is-expanded" : "is-collapsed"}`} data-testid="status-bar">
      {/* Always-visible single rollup pill — click to expand the full strip */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className={`status-pill ${isLive ? "is-live" : "is-demo"}`}
        data-testid="status-rollup"
        title={expanded ? "Click to collapse" : "Click to see all status pills"}
      >
        {isLive ? "● LIVE" : "○ DEMO"}{anyOffline && !expanded ? " · ⚠" : ""}
        <span className="status-rollup-chev">{expanded ? "▴" : "▾"}</span>
      </button>
      {/* Expanded set — only renders when expanded so it never obstructs panels */}
      {expanded && (
        <>
          <button
            type="button"
            onClick={flipMode}
            disabled={busy}
            className={`status-pill ${isLive ? "is-live" : "is-demo"}`}
            title={`Click to flip to ${isLive ? "DEMO" : "LIVE"}`}
            data-testid="status-trade-mode"
          >
            {isLive ? "● FLIP TO DEMO" : "○ FLIP TO LIVE"}
          </button>
          <span className={`status-pill ${broker === "ONLINE" ? "is-ok" : "is-warn"}`} data-testid="status-broker">
            BROKER · {broker}
          </span>
          {voiceName && (
            <span className="status-pill" data-testid="status-voice">VOICE · {voiceName.toUpperCase()}</span>
          )}
          {status.telegram_configured ? (
            <span className="status-pill is-ok" data-testid="status-telegram">TG ✓</span>
          ) : (
            <span className="status-pill is-dim" data-testid="status-telegram" title="Telegram bot key not set">TG —</span>
          )}
          {status.raw_hub_configured ? (
            <button
              type="button"
              onClick={() => onOpenPanel?.({ type: "raw_hub_dashboard" })}
              className="status-pill is-ok"
              data-testid="status-raw-hub"
              title="Click to open the RAW Hub mirror panel"
            >HUB ✓</button>
          ) : (
            <span className="status-pill is-dim" data-testid="status-raw-hub" title="RAW hub keys not set">HUB —</span>
          )}
          <button
            type="button"
            onClick={() => onOpenPanel?.({ type: "settings" })}
            className="status-pill"
            data-testid="status-settings"
            title="Open settings"
          >⚙ SETTINGS</button>
        </>
      )}
    </div>
  );
}
